import { useContext } from 'react'

import { digObject, isValidPrice } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCampaignItem, { useCampaignItemForm } from '@hooks/useCampaignItem'

import CustomItemSearch from '@components/CustomItemSearch'

const MODAL_KEY = 'CreateOrEditCampaignItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignItem: (payload: HandleCallbackActionParams) => void,
  updateCampaignItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignItem, updateCampaignItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const campaignItem = digObject(modalPayload, 'campaignItem', {})

  const newRecord = !campaignItem.id

  const {
    callbacks: {
      createCampaignItem: createFn,
      updateCampaignItem: updateFn,
    },
    creating,
    updating,
  } = useCampaignItem(campaignItem)

  const {
    entityState,
    entityState: {
      price,
      title,
    },
    errors,
    saveEnabled,
    setEntityState,
  } = useCampaignItemForm(campaignItem, { validateOn: 'change' })

  const createOrUpdateCampaignItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Campaign Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Campaign Item"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <CustomItemSearch
              callbacks={{
                onChange: value => setEntityState({ title: value }),
                selectItem: item => setEntityState({
                  custom_item_id: item.id,
                  price: item.price,
                  price_override: isValidPrice(item.price_override) ? item.price_override : item.price,
                  title: item.title,
                }),
              }}
              value={title}
            />
          </Form.Field>

          <Form.Field label="* Price" errorMessage={errors.title} marginTop="large">
            <input
              name="price"
              onChange={e => setEntityState({
                price: e.target.value,
                price_override: e.target.value,
              })}
              type="number"
              value={price}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignItem(createOrUpdateCampaignItemPayload)
              : () => updateCampaignItem(createOrUpdateCampaignItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Campaign Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
